if (!customElements.get('product-multi-step-configurator')) {
  customElements.define(
    'product-multi-step-configurator',
    class ProductMultiStepConfigurator extends HTMLElement {
      constructor() {
        super();
        this._onNext = this._onNext.bind(this);
        this._onBack = this._onBack.bind(this);
        this._onOptionChange = this._onOptionChange.bind(this);
        this._onAddToCart = this._onAddToCart.bind(this);
      }

      connectedCallback() {
        this._setup();
        requestAnimationFrame(() => this._setup());
        this.closest('product-info')?.addEventListener('product-info:loaded', () => {
          this._setup();
        }, { once: false });

        // Re-run setup when Shopify replaces variant-selects via HTMLUpdateUtility.viewTransition
        this._vsObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'variant-selects') {
                this._setup();
                return;
              }
            }
          }
        });
        this._vsObserver.observe(this, { childList: true });
      }

      disconnectedCallback() {
        this._vsObserver?.disconnect();
        this._nextBtn?.removeEventListener('click', this._onNext);
        this._backBtn?.removeEventListener('click', this._onBack);
        this._variantSelects?.removeEventListener('change', this._onOptionChange);
        this._addToCartBtn?.removeEventListener('click', this._onAddToCart);
      }

      get sectionId() {
        return this.dataset.sectionId;
      }

      get productForm() {
        const info = this.closest('product-info');
        const form = info?.querySelector(`#product-form-${this.sectionId}`);
        if (form) return form.closest('product-form');
        return document.querySelector(`product-form[data-section-id="${this.sectionId}"]`);
      }

      get w3Container() {
        return this.productForm?.querySelector('.w3-product-options');
      }

      _setup() {
        this._variantSelects = this.querySelector('variant-selects');
        this._nextBtn = this.querySelector('.product-multi-step__next');
        this._backBtn = this.querySelector('.product-multi-step__back');
        this._progressFill = this.querySelector('.product-multi-step__progress-fill');
        this._progressBar = this.querySelector('.product-multi-step__progress');
        this._stepLabelEl = this.querySelector('.product-multi-step__step-label');
        this._actionLabelEl = this.querySelector('.product-multi-step__action-label');
        this._titleStepEl = this.querySelector('.product-multi-step__title-step');
        this._appointmentBtn = this.querySelector('.product-multi-step__appointment');

        if (!this._variantSelects) {
          if (!this._setupPolling) this._pollForSetup();
          return;
        }
        this._setupPolling = false;

        this._nextBtn?.removeEventListener('click', this._onNext);
        this._backBtn?.removeEventListener('click', this._onBack);
        this._variantSelects.removeEventListener('change', this._onOptionChange);
        this._addToCartBtn?.removeEventListener('click', this._onAddToCart);

        this._nextBtn?.addEventListener('click', this._onNext);
        this._backBtn?.addEventListener('click', this._onBack);
        this._variantSelects.addEventListener('change', this._onOptionChange);
        this._addToCartBtn = this.querySelector('.product-multi-step__add-to-cart');
        this._addToCartBtn?.addEventListener('click', this._onAddToCart);

        this._unwrapPanels();
        this._panels = [];
        this._enhanceSliders();

        // Wrap loose variant fieldsets (direct children of variant-selects that aren't already panels)
        const inputs = this._variantSelects.querySelectorAll(':scope > .product-form__input');
        const ringKeyword = (this.dataset.ringKeyword || 'ring size').toLowerCase();
        const includeW3 = this.dataset.includeW3Step === 'true';
        const hasW3 = !!(includeW3 && this.w3Container);

        inputs.forEach((el) => {
          const wrap = document.createElement('div');
          wrap.className = 'product-multi-step__panel';
          el.parentNode.insertBefore(wrap, el);
          wrap.appendChild(el);
        });

        // W3 extra step (only if last variant isn't ring size and no native ring size panel exists)
        const nativeRingSizePanel = this._variantSelects.querySelector(
          '.product-multi-step__panel--static.product-multi-step__panel--property[data-property-name="Ring Size"]'
        );
        const lastInput = inputs[inputs.length - 1];
        const lastName = lastInput?.dataset.optionName?.toLowerCase() || '';
        const lastIsRing = lastName.includes(ringKeyword);

        this._extraW3OnlyStep = hasW3 && !lastIsRing && !nativeRingSizePanel;
        if (this._extraW3OnlyStep) {
          const wrap = document.createElement('div');
          wrap.className = 'product-multi-step__panel product-multi-step__panel--w3';
          wrap.appendChild(this._buildRingStepHeader());
          const placeholder = document.createElement('div');
          placeholder.className = 'product-multi-step__w3-placeholder';
          wrap.appendChild(placeholder);
          this._variantSelects.appendChild(wrap);
        }

        // Collect all panels in DOM order; skip-step panels are hidden auto-selects
        const allPanels = Array.from(
          this._variantSelects.querySelectorAll(':scope > .product-multi-step__panel')
        );

        // Auto-select and hide skip-step panels (e.g. side diamond type)
        allPanels.filter(p => p.dataset.skipStep).forEach(panel => {
          const val = panel.dataset.autoSelectValue;
          const radio = val
            ? panel.querySelector(`input[type="radio"][value="${val}"]`)
            : panel.querySelector('input[type="radio"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: false }));
          }
        });

        this._panels = allPanels.filter(p => !p.dataset.skipStep);
        this._totalSteps = this._panels.length;

        if (this._prevPanelCount !== undefined && this._prevPanelCount !== this._totalSteps) {
          this._currentStep = 1;
        }
        this._prevPanelCount = this._totalSteps;
        this._currentStep = Math.min(this._currentStep || 1, this._totalSteps);

        this._progressBar?.setAttribute('aria-valuemax', String(this._totalSteps));

        this._setupPropertySync();
        this._restoreNativeOptionSelection();
        this._enhanceCaratStep();
        this._setupModalHandlers();

        this._renderStep();
        this._syncProductFormMode();

        const includeW3Setting = this.dataset.includeW3Step === 'true';
        if (this.w3Container) {
          this._w3Retry = 0;
        } else if (includeW3Setting && !this._w3Polling) {
          this._pollForW3();
        }
      }

      _pollForSetup() {
        this._setupPolling = true;
        const start = performance.now();
        const tick = () => {
          if (!this.isConnected) { this._setupPolling = false; return; }
          const vs = this.querySelector('variant-selects');
          if (vs && vs.querySelector('.product-multi-step__panel, .product-form__input')) {
            this._setupPolling = false;
            this._setup();
            return;
          }
          if (performance.now() - start > 10000) { this._setupPolling = false; return; }
          setTimeout(tick, 100);
        };
        setTimeout(tick, 100);
      }

      _pollForW3() {
        this._w3Polling = true;
        const start = performance.now();
        const tick = () => {
          if (!this.isConnected) { this._w3Polling = false; return; }
          if (this.w3Container) { this._w3Polling = false; this._setup(); return; }
          if (performance.now() - start > 20000) { this._w3Polling = false; return; }
          setTimeout(tick, 250);
        };
        setTimeout(tick, 250);
      }

      _buildRingStepHeader() {
        const header = document.createElement('div');
        header.className = 'product-multi-step__ring-header';
        const label = document.createElement('span');
        label.className = 'product-multi-step__ring-header-label';
        label.textContent = (this.dataset.ringStepLabel || 'Ring Size').toUpperCase();
        header.appendChild(label);
        const opener = document.createElement('modal-opener');
        opener.setAttribute('data-modal', `#RingSizeGuideModal-${this.sectionId}`);
        opener.className = 'product-multi-step__ring-header-link';
        opener.innerHTML = `<button type="button" class="link"><svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14"><path fill="currentColor" d="M3 7h18v10H3V7zm2 2v6h14V9h-2v3h-1V9h-2v2h-1V9h-2v3h-1V9H9v2H8V9H5z"/></svg><span>Size Guide</span></button>`;
        header.appendChild(opener);
        return header;
      }

      _unwrapPanels() {
        const vs = this._variantSelects;
        if (!vs) return;
        // Only unwrap JS-created panels, not static Liquid-rendered ones
        vs.querySelectorAll(
          ':scope > .product-multi-step__panel:not(.product-multi-step__panel--static):not(.product-multi-step__panel--w3)'
        ).forEach((panel) => {
          while (panel.firstChild) {
            panel.parentNode.insertBefore(panel.firstChild, panel);
          }
          panel.remove();
        });
        // Remove W3-only panels so they get rebuilt fresh
        vs.querySelectorAll(':scope > .product-multi-step__panel--w3').forEach(p => p.remove());
        vs.querySelectorAll(
          ':scope > .product-multi-step__ring-header, :scope > .product-multi-step__w3-placeholder'
        ).forEach((el) => el.remove());
        this._panels = [];
      }

      _enhanceSliders() {
        this.querySelectorAll('[data-multi-step-slider]').forEach((fieldset) => {
          if (fieldset.querySelector('.product-form__input--slider__track')) return;

          const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
          const usable = radios.filter((r) => !r.classList.contains('disabled') && !r.disabled);
          if (usable.length < 2) return;

          usable.forEach((r) => {
            const label = fieldset.querySelector(`label[for="${r.id}"]`);
            if (label) label.classList.add('product-form__input--slider__value-label');
          });

          const selectedIdx = Math.max(0, usable.findIndex((r) => r.checked));

          const track = document.createElement('div');
          track.className = 'product-form__input--slider__track';

          const range = document.createElement('input');
          range.type = 'range';
          range.min = '0';
          range.max = String(usable.length - 1);
          range.step = '1';
          range.value = String(selectedIdx < 0 ? 0 : selectedIdx);
          range.className = 'product-form__input--slider__range';
          range.setAttribute('aria-label', fieldset.dataset.optionName || 'Option');

          const ticks = document.createElement('div');
          ticks.className = 'product-form__input--slider__ticks';
          usable.forEach((r, i) => {
            const tick = document.createElement('button');
            tick.type = 'button';
            tick.className = 'product-form__input--slider__tick';
            tick.dataset.index = String(i);
            tick.textContent = r.value;
            tick.addEventListener('click', () => {
              range.value = String(i);
              this._selectSliderValue(fieldset, usable, i);
            });
            ticks.appendChild(tick);
          });

          range.addEventListener('input', () => {
            this._selectSliderValue(fieldset, usable, parseInt(range.value, 10), { commit: false });
          });
          range.addEventListener('change', () => {
            this._selectSliderValue(fieldset, usable, parseInt(range.value, 10), { commit: true });
          });

          track.appendChild(range);
          track.appendChild(ticks);
          fieldset.appendChild(track);
          this._refreshSliderState(fieldset);
        });
      }

      _selectSliderValue(fieldset, usable, idx, { commit = true } = {}) {
        const target = usable[idx];
        if (!target) return;
        if (!target.checked) {
          target.checked = true;
          if (commit) target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this._refreshSliderState(fieldset);
      }

      _refreshSliderState(fieldset) {
        const ticks = fieldset.querySelectorAll('.product-form__input--slider__tick');
        const range = fieldset.querySelector('.product-form__input--slider__range');
        const selVal = fieldset.querySelector('[data-selected-value]');
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        let idx = range ? parseInt(range.value, 10) : 0;
        if (checked) {
          const matching = Array.from(
            fieldset.querySelectorAll('input[type="radio"]:not(.disabled):not([disabled])')
          ).indexOf(checked);
          if (matching >= 0) idx = matching;
          if (selVal) selVal.textContent = checked.value;
        }
        if (range) range.value = String(idx);
        ticks.forEach((t, i) => {
          t.classList.toggle('product-form__input--slider__tick--active', i === idx);
        });
      }

      // Inject "What is this?" opener into the carat step legend
      _enhanceCaratStep() {
        const caratKey = (this.dataset.caratKeyword || '').toLowerCase();
        if (!caratKey) return;
        const modalId = `CaratInfoModal-${this.sectionId}`;
        if (!document.getElementById(modalId)) return;

        this._panels.forEach(panel => {
          const inner = panel.querySelector('.product-form__input');
          if (!inner) return;
          const name = (inner.dataset.optionName || this._optionDisplayName(inner)).toLowerCase();
          if (!name.includes(caratKey)) return;
          const legend = inner.querySelector('legend') || inner.querySelector('.product-form__input__legend');
          if (!legend || legend.querySelector('[data-carat-modal-trigger]')) return;
          const caratInfoText = this.dataset.caratInfoText || 'What is this?';
          const opener = document.createElement('modal-opener');
          opener.setAttribute('data-modal', `#${modalId}`);
          opener.className = 'product-form__input__helper-opener';
          opener.innerHTML = `<button type="button" class="product-form__input__helper link" data-carat-modal-trigger>${caratInfoText}</button>`;
          legend.appendChild(opener);
        });
      }

      // Property sync: tracks configurator UI choices for direct cart submission
      _setupPropertySync() {
        if (!this._properties) this._properties = {};
        this._bandVariantId = null;
        // Standalone checkboxes with data-property-name (e.g. wedding band toggle)
        this._variantSelects.querySelectorAll('input[type="checkbox"][data-property-name]').forEach(cb => {
          const bandVariantId = cb.dataset.bandVariantId;
          if (bandVariantId) {
            // Band toggle — add as separate cart item, not a line-item property
            this._bandVariantId = cb.checked ? bandVariantId : null;
            cb.addEventListener('change', (e) => {
              e.stopPropagation();
              // Read from dataset so metal-sync updates are respected
              this._bandVariantId = cb.checked ? (cb.dataset.bandVariantId || null) : null;
              this._renderStep();
            });
          } else {
            const propName = cb.dataset.propertyName;
            this._syncProperty(propName, cb.checked ? cb.value : '');
            cb.addEventListener('change', (e) => {
              e.stopPropagation();
              this._syncProperty(propName, cb.checked ? cb.value : '');
              this._renderStep();
            });
          }
        });

        // Static panels with data-property-name drive line-item properties
        this._variantSelects.querySelectorAll('.product-multi-step__panel--property[data-property-name]').forEach(panel => {
          const propName = panel.dataset.propertyName;

          const checkedRadio = panel.querySelector('input[type="radio"]:checked');
          const checkedBox = panel.querySelector('input[type="checkbox"]');
          const selValEl = panel.querySelector('[data-selected-value]');
          if (selValEl && checkedRadio) selValEl.textContent = checkedRadio.value;

          // Radios with data-option-value-id are native variant options (e.g. diamond shape).
          // Skip line-item property tracking; dispatch on variant-selects for gallery refresh instead.
          const isNativeVariantPanel = !!panel.querySelector('input[type="radio"][data-option-value-id]');

          if (!isNativeVariantPanel) {
            if (checkedRadio) this._syncProperty(propName, checkedRadio.value);
            else if (checkedBox) this._syncProperty(propName, checkedBox.checked ? checkedBox.value : '');
          }

          panel.querySelectorAll('input[type="radio"]').forEach(radio => {
            radio.addEventListener('change', (e) => {
              e.stopPropagation();
              if (!isNativeVariantPanel) this._syncProperty(propName, radio.value);
              if (selValEl) selValEl.textContent = radio.value;
              // :has() fallback for older browsers
              panel.querySelectorAll('.product-form__input--ring-size__pill').forEach(pill => {
                pill.classList.toggle('product-form__input--ring-size__pill--selected', pill.contains(radio) && radio.checked);
              });
              if (isNativeVariantPanel) {
                this._selectedShapeValue = radio.value;
                this._variantSelects?.dispatchEvent(new Event('change', { bubbles: true }));
              }
              this._renderStep();
            });
          });
          // Set initial selected class
          panel.querySelectorAll('.product-form__input--ring-size__pill').forEach(pill => {
            const r = pill.querySelector('input[type="radio"]');
            if (r) pill.classList.toggle('product-form__input--ring-size__pill--selected', r.checked);
          });

          panel.querySelectorAll('input[type="checkbox"]').forEach(cb => {
            cb.addEventListener('change', (e) => {
              e.stopPropagation();
              this._syncProperty(propName, cb.checked ? cb.value : '');
              this._renderStep();
            });
          });
        });

        // Parse band variants for metal-aware sync
        const bandPanel = this._variantSelects.querySelector('.product-multi-step__panel--wedding-band');
        if (bandPanel) {
          const scriptEl = bandPanel.querySelector('script[data-band-variants]');
          try { this._bandVariants = JSON.parse(scriptEl?.textContent || '[]'); } catch (e) { this._bandVariants = []; }
          this._bandMetalOptionPos = parseInt(bandPanel.dataset.bandMetalOptionPos ?? '-1', 10);
          this._bandProductTitle = bandPanel.dataset.bandProductTitle || '';
          this._syncBandVariant();
        }
      }

      _syncProperty(name, value) {
        if (!this._properties) this._properties = {};
        this._properties[name] = value;
      }

      _syncBandVariant() {
        if (!this._bandVariants?.length) return;

        let currentVariant = null;
        try {
          const el = this._variantSelects?.querySelector('[data-selected-variant]');
          if (el) currentVariant = JSON.parse(el.textContent);
        } catch (e) {}
        if (!currentVariant?.options) return;

        const metalPos = this._bandMetalOptionPos ?? -1;
        if (metalPos < 0) return;

        const metalValue = currentVariant.options[metalPos];
        if (!metalValue) return;

        const match = this._bandVariants.find(v => v.options.some(o => o === metalValue));
        if (!match) return;

        const sid = this.sectionId;

        const cardImg = document.getElementById(`WeddingBandCardImg-${sid}`);
        if (cardImg) cardImg.src = match.image;

        const cardPrice = document.getElementById(`WeddingBandPrice-${sid}`);
        if (cardPrice) cardPrice.textContent = `+${match.price}`;

        const toggle = document.getElementById(`WeddingBandToggle-${sid}`);
        if (toggle) {
          toggle.dataset.bandVariantId = String(match.id);
          if (toggle.checked) this._bandVariantId = String(match.id);
        }

        const modalImg = document.getElementById(`WeddingBandModalImg-${sid}`);
        if (modalImg) modalImg.src = match.image_lg;

        const modalPrice = document.getElementById(`WeddingBandModalPrice-${sid}`);
        if (modalPrice) modalPrice.textContent = match.price;

        const addBtn = document.getElementById(`WeddingBandModalAdd-${sid}`);
        if (addBtn) addBtn.innerHTML = `+ Add to Set &mdash; ${match.price}`;

        const viewBtn = document.getElementById(`WeddingBandModalView-${sid}`);
        if (viewBtn && match.url) viewBtn.href = `${match.url}?variant=${match.id}`;

        const cardDesc = document.getElementById(`WeddingBandCardDesc-${sid}`);
        if (cardDesc) cardDesc.textContent = `Complete the set with a perfectly matched ${metalValue} ${this._bandProductTitle}`.trim();

        const modalTitle = document.getElementById(`WeddingBandModalTitle-${sid}`);
        if (modalTitle) modalTitle.textContent = metalValue ? `${metalValue} ${this._bandProductTitle}` : this._bandProductTitle;
      }

      // Wire up modal close buttons and "Add to Set" button in wedding band modal.
      // modal-dialog.connectedCallback() moves elements to document.body, so we search by ID.
      _setupModalHandlers() {
        const sid = this.sectionId;
        [
          `RingSizeGuideModal-${sid}`,
          `CaratInfoModal-${sid}`,
          `WeddingBandModal-${sid}`,
        ].forEach(id => {
          const dialog = document.getElementById(id);
          if (!dialog) return;

          dialog.querySelectorAll(
            '.product-multi-step__modal-got-it, .product-multi-step__modal-close-btn'
          ).forEach(btn => {
            btn.addEventListener('click', () => dialog.hide?.());
          });

          dialog.querySelectorAll('[data-band-add-toggle]').forEach(btn => {
            btn.addEventListener('click', () => {
              const toggle = this.querySelector(`#WeddingBandToggle-${btn.dataset.bandAddToggle}`);
              if (toggle) {
                toggle.checked = true;
                toggle.dispatchEvent(new Event('change', { bubbles: true }));
              }
              dialog.hide?.();
            });
          });
        });
      }

      _panelDisplayName(panel) {
        if (panel.dataset.optionLabel) return panel.dataset.optionLabel;
        const inner = panel.querySelector('.product-form__input');
        return inner ? this._optionDisplayName(inner) : '';
      }

      _optionDisplayName(el) {
        const fromData = el.dataset.optionName;
        if (fromData) return fromData;
        const leg = el.querySelector('legend');
        if (leg) return leg.textContent.replace(/:\s*$/, '').trim();
        const lab = el.querySelector('label.form__label');
        if (lab) return lab.textContent.trim();
        return '';
      }

      _nextActionLabel(stepIndex) {
        if (stepIndex >= this._totalSteps - 1) return '';
        const nextPanel = this._panels[stepIndex + 1];
        if (!nextPanel) return '';
        if (nextPanel.classList.contains('product-multi-step__panel--w3')) return 'Ring Size';
        return this._panelDisplayName(nextPanel) || 'option';
      }

      _renderStep() {
        if (!this._panels?.length) return;

        // Clear ring-size error state once a selection exists
        this._panels.forEach(p => {
          if (p.classList.contains('product-multi-step__panel--needs-selection')
              && p.querySelector('input[type="radio"]:checked')) {
            p.classList.remove('product-multi-step__panel--needs-selection');
          }
        });

        const idx = this._currentStep - 1;
        this._panels.forEach((panel, i) => {
          panel.classList.toggle('product-multi-step__panel--active', i === idx);
          panel.hidden = i !== idx;
        });

        const stepName = this._panelDisplayName(this._panels[idx]) || 'Ring Size';
        if (this._titleStepEl) this._titleStepEl.textContent = stepName;

        if (this._stepLabelEl) {
          this._stepLabelEl.textContent = `STEP ${this._currentStep} OF ${this._totalSteps}`;
        }

        const pct = (this._currentStep / this._totalSteps) * 100;
        if (this._progressFill) this._progressFill.style.width = `${pct}%`;
        this._progressBar?.setAttribute('aria-valuenow', String(this._currentStep));

        const onLast = this._currentStep >= this._totalSteps;
        const nextName = this._nextActionLabel(idx);
        if (this._actionLabelEl) {
          this._actionLabelEl.textContent = stepName ? `Choose ${stepName}` : '';
        }
        if (this._nextBtn) {
          this._nextBtn.hidden = onLast;
          const nextText = this._nextBtn.querySelector('.product-multi-step__next-text');
          if (nextText && !onLast) {
            const nextPanel = this._panels[idx + 1];
            const nextIsWeddingBand = nextPanel?.classList.contains('product-multi-step__panel--wedding-band');
            nextText.textContent = nextIsWeddingBand
              ? 'Next, Complete Your Set (Optional)'
              : nextName ? `Next, choose ${nextName}` : 'Next';
          }
        }
        if (this._backBtn) {
          this._backBtn.hidden = this._currentStep <= 1;
        }
        if (this._addToCartBtn) {
          this._addToCartBtn.hidden = !onLast;
          if (onLast) {
            const submitBtn = this.productForm?.querySelector('[name="add"]');
            this._addToCartBtn.disabled = submitBtn?.disabled ?? false;
          }
        }
        if (this._appointmentBtn) {
          this._appointmentBtn.hidden = !onLast;
        }

        const w3 = this.w3Container;
        const includeW3 = this.dataset.includeW3Step === 'true';
        if (w3 && includeW3) {
          w3.classList.toggle('w3-product-options--multi-step-hidden', !onLast);
        }
      }

      _onOptionChange(event) {
        const fieldset = event?.target?.closest?.('[data-multi-step-slider]');
        if (fieldset) this._refreshSliderState(fieldset);
        this._syncBandVariant();
        this._renderStep();
      }

      _isRingSizePanel(panel) {
        if (!panel) return false;
        const propName = (panel.dataset.propertyName || '').toLowerCase();
        const ringKeyword = (this.dataset.ringKeyword || 'ring size').toLowerCase();
        return panel.classList.contains('product-multi-step__panel--property') && propName.includes(ringKeyword);
      }

      _onNext() {
        if (this._currentStep < this._totalSteps) {
          const panel = this._panels[this._currentStep - 1];
          if (this._isRingSizePanel(panel) && !panel.querySelector('input[type="radio"]:checked')) {
            panel.classList.remove('product-multi-step__panel--needs-selection');
            void panel.offsetWidth;
            panel.classList.add('product-multi-step__panel--needs-selection');
            return;
          }
          this._currentStep += 1;
          this._renderStep();
        }
      }

      _onBack() {
        if (this._currentStep > 1) {
          this._currentStep -= 1;
          this._renderStep();
        }
      }

      _onAddToCart() {
        let variant = null;
        try {
          const el = this._variantSelects?.querySelector('[data-selected-variant]');
          if (el) variant = JSON.parse(el.textContent);
        } catch (e) {}

        const variantId = variant?.id
          || new URLSearchParams(window.location.search).get('variant');

        if (!variantId) return;

        this._addToCartBtn?.setAttribute('aria-disabled', 'true');

        const properties = Object.fromEntries(
          Object.entries(this._properties || {}).filter(([, v]) => v)
        );
        const items = [{ id: Number(variantId), quantity: 1, properties }];
        if (this._bandVariantId) {
          items.push({ id: Number(this._bandVariantId), quantity: 1 });
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items }),
        })
          .then(r => r.json())
          .then(data => {
            if (data.status) throw new Error(data.description || 'Cart error');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-multi-step-configurator',
              productVariantId: variantId,
              cartData: data,
            });
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`)
                .then(r => r.text())
                .then(html => {
                  const doc = new DOMParser().parseFromString(html, 'text/html');
                  const newDrawer = doc.querySelector('cart-drawer');
                  if (newDrawer) {
                    cartDrawer.innerHTML = newDrawer.innerHTML;
                    cartDrawer.classList.remove('is-empty');
                    cartDrawer.open?.();
                  }
                });
            } else {
              document.querySelector('cart-notification')?.renderContents?.(data);
            }
          })
          .catch(err => console.error('[MSC] cart add failed:', err))
          .finally(() => this._addToCartBtn?.removeAttribute('aria-disabled'));
      }

      _restoreNativeOptionSelection() {
        if (!this._selectedShapeValue) return;
        this._variantSelects?.querySelectorAll(
          '.product-multi-step__panel--property[data-property-name] input[type="radio"][data-option-value-id]'
        ).forEach(r => { r.checked = r.value === this._selectedShapeValue; });
      }

      _syncProductFormMode() {}
    }
  );
}
